/**
 * Layout and text primitives (ADR-061) — the replacements for MUI's
 * `Stack`, `Typography`, `Box`, `Divider`, `CircularProgress` and
 * `IconButton`, which between them account for over 1,300 of the
 * application's MUI imports.
 *
 * These are intentionally thin. A primitive that accepts fifty props is
 * a styling language of its own, and the point of moving to Tailwind is
 * that the styling language is already there — so each of these owns
 * exactly one thing (spacing rhythm, type scale, control geometry) and
 * forwards `className` for everything else.
 */
import { forwardRef, type ButtonHTMLAttributes, type ElementType, type HTMLAttributes, type JSX, type ReactNode } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@utils/cn';
import { Icon, type MaterialSymbolName } from './icon';

/* ------------------------------------------------------------------ *
 * Stack — MUI's most-used component (416 imports)
 * ------------------------------------------------------------------ */

/** The 4px spacing rhythm. `gap={2}` is 8px, matching MUI's `spacing={1}` so migrated call sites keep their proportions. */
const GAP: Record<number, string> = {
  0: 'tw:gap-0',
  1: 'tw:gap-1',
  2: 'tw:gap-2',
  3: 'tw:gap-3',
  4: 'tw:gap-4',
  5: 'tw:gap-5',
  6: 'tw:gap-6',
};

export interface StackProps extends HTMLAttributes<HTMLElement> {
  direction?: 'row' | 'column';
  gap?: 0 | 1 | 2 | 3 | 4 | 5 | 6;
  align?: 'start' | 'center' | 'end' | 'stretch' | 'baseline';
  justify?: 'start' | 'center' | 'end' | 'between' | 'around';
  wrap?: boolean;
  as?: ElementType;
}

const ALIGN = { start: 'tw:items-start', center: 'tw:items-center', end: 'tw:items-end', stretch: 'tw:items-stretch', baseline: 'tw:items-baseline' };
const JUSTIFY = { start: 'tw:justify-start', center: 'tw:justify-center', end: 'tw:justify-end', between: 'tw:justify-between', around: 'tw:justify-around' };

export function Stack({
  direction = 'column',
  gap = 2,
  align,
  justify,
  wrap,
  as: Component = 'div',
  className,
  ...props
}: StackProps): JSX.Element {
  return (
    <Component
      className={cn(
        'tw:flex tw:min-w-0',
        direction === 'row' ? 'tw:flex-row' : 'tw:flex-col',
        GAP[gap],
        align && ALIGN[align],
        justify && JUSTIFY[justify],
        wrap && 'tw:flex-wrap',
        className,
      )}
      {...props}
    />
  );
}

/* ------------------------------------------------------------------ *
 * Text — replaces Typography (249 imports)
 * ------------------------------------------------------------------ */

/**
 * A deliberately small type scale. MUI ships thirteen `variant`s and
 * this application used a handful of them inconsistently; five sizes
 * with one meaning each is what a dense operational UI actually needs.
 */
const textVariants = cva('', {
  variants: {
    variant: {
      /** Page/section heading. */
      heading: 'tw:text-sm tw:font-bold tw:uppercase tw:tracking-wider tw:text-nx-text',
      /** Card or block title. */
      title: 'tw:text-sm tw:font-semibold tw:text-nx-text',
      /** Default reading size. */
      body: 'tw:text-xs tw:text-nx-text',
      /** Supporting detail, labels. */
      caption: 'tw:text-label tw:text-nx-text-muted',
      /** Least prominent — hints, disclosures, empty states. */
      hint: 'tw:text-label tw:text-nx-text-faint',
      /** A figure that should read as a figure. Tabular so digits align in a column. */
      metric: 'tw:text-xl tw:font-semibold tw:tabular-nums tw:text-nx-text',
    },
    tone: {
      default: '',
      muted: 'tw:text-nx-text-muted',
      faint: 'tw:text-nx-text-faint',
      error: 'tw:text-nx-error',
      warning: 'tw:text-nx-text',
      success: 'tw:text-nx-success',
      primary: 'tw:text-nx-primary',
    },
    truncate: { true: 'tw:truncate', false: '' },
  },
  defaultVariants: { variant: 'body', tone: 'default', truncate: false },
});

export interface TextProps extends Omit<HTMLAttributes<HTMLElement>, 'color'>, VariantProps<typeof textVariants> {
  as?: ElementType;
}

export function Text({ variant, tone, truncate, as: Component = 'span', className, ...props }: TextProps): JSX.Element {
  return <Component className={cn(textVariants({ variant, tone, truncate }), className)} {...props} />;
}

/* ------------------------------------------------------------------ *
 * Divider, Spinner, IconButton
 * ------------------------------------------------------------------ */

export function Divider({ vertical, className }: { vertical?: boolean; className?: string }): JSX.Element {
  return (
    <hr
      aria-orientation={vertical ? 'vertical' : 'horizontal'}
      className={cn('tw:border-0 tw:bg-nx-border', vertical ? 'tw:h-full tw:w-px' : 'tw:h-px tw:w-full', className)}
    />
  );
}

/**
 * Replaces `CircularProgress`. `role="status"` + a visually-hidden label
 * so a screen reader announces that something is loading — an unlabelled
 * spinner is silence to a non-sighted user.
 */
export function Spinner({ size = 16, label = 'Loading', className }: { size?: number; label?: string; className?: string }): JSX.Element {
  return (
    <span role="status" className={cn('tw:inline-flex tw:items-center', className)}>
      <svg
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="none"
        aria-hidden
        className="tw:animate-spin tw:text-nx-primary tw:motion-reduce:animate-none"
      >
        <circle cx="12" cy="12" r="9" stroke="currentColor" strokeOpacity="0.2" strokeWidth="3" />
        <path d="M21 12a9 9 0 0 0-9-9" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
      </svg>
      <span className="tw:sr-only">{label}</span>
    </span>
  );
}

/**
 * Replaces `IconButton`. `label` is REQUIRED, not optional: an icon-only
 * button with no accessible name is the single most common a11y defect
 * in an operational UI, and making the prop mandatory means it cannot be
 * forgotten rather than merely discouraged.
 */
export interface IconButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children'> {
  icon: MaterialSymbolName;
  label: string;
  size?: 'sm' | 'md';
  tone?: 'default' | 'primary' | 'error';
}

export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(function IconButton(
  { icon, label, size = 'md', tone = 'default', className, ...props },
  ref,
) {
  const box = size === 'sm' ? 'tw:size-6' : 'tw:size-8';
  const toneClass =
    tone === 'primary' ? 'tw:text-nx-primary' : tone === 'error' ? 'tw:text-nx-error' : 'tw:text-nx-text-muted';
  return (
    <button
      ref={ref}
      type="button"
      aria-label={label}
      title={label}
      className={cn(
        'nx-btn nx-focusable tw:inline-flex tw:shrink-0 tw:items-center tw:justify-center tw:rounded tw:transition-colors tw:duration-200 tw:hover:bg-nx-surface-hover tw:disabled:opacity-40',
        box,
        toneClass,
        className,
      )}
      {...props}
    >
      <Icon name={icon} size={size === 'sm' ? 14 : 18} />
    </button>
  );
});

/** Replaces `Chip`. Uses the five families by name, never a raw colour. */
const badgeVariants = cva(
  'tw:inline-flex tw:items-center tw:gap-1 tw:rounded tw:px-1.5 tw:py-0.5 tw:text-label tw:font-semibold tw:whitespace-nowrap',
  {
    variants: {
      family: {
        blue: 'tw:bg-[var(--nx-blue-tint)] tw:text-[var(--nx-blue-on-tint)]',
        red: 'tw:bg-[var(--nx-red-tint)] tw:text-[var(--nx-red-on-tint)]',
        yellow: 'tw:bg-[var(--nx-yellow-tint)] tw:text-[var(--nx-yellow-on-tint)]',
        green: 'tw:bg-[var(--nx-green-tint)] tw:text-[var(--nx-green-on-tint)]',
        grey: 'tw:bg-[var(--nx-grey-tint)] tw:text-[var(--nx-grey-on-tint)]',
      },
      outlined: { true: 'tw:bg-transparent tw:border', false: '' },
    },
    defaultVariants: { family: 'grey', outlined: false },
  },
);

export interface TagProps extends HTMLAttributes<HTMLSpanElement>, VariantProps<typeof badgeVariants> {
  icon?: MaterialSymbolName;
  children: ReactNode;
}

export function Tag({ family, outlined, icon, children, className, ...props }: TagProps): JSX.Element {
  return (
    <span className={cn(badgeVariants({ family, outlined }), className)} {...props}>
      {icon && <Icon name={icon} size={12} />}
      {children}
    </span>
  );
}
